// ============================================================================
// Instagram DM — user profile lookup.
//
// Given an IGSID (Instagram-scoped user id, 17-digit numeric), fetch the
// subset of profile fields our messaging capability exposes. Instagram is
// stingy about profile fields: `name`, `username`, `profile_pic` are all the
// Messaging API exposes, and even those require the business to hold
// `instagram_business_manage_messages` AND be in an active DM thread with the
// user.
//
// The returned `UserProfile` is the capability-level shape (see
// `../../capabilities/messaging.ts`) — callers should not depend on the raw
// Graph response format.
// ============================================================================

import type {
  FetchUserProfileArgs,
  UserProfile,
} from '../../capabilities/messaging'
import {
  InstagramDmError,
  classifyInstagramError,
  parseInstagramRetryAfterMs,
  type InstagramErrorEnvelope,
  type InstagramUserProfile,
} from './types'

// ============================================================================
// Constants
// ============================================================================

const INSTAGRAM_GRAPH_API_BASE = 'https://graph.instagram.com/v21.0'
const PROFILE_FIELDS = 'name,username,profile_pic'

// ============================================================================
// getInstagramUserProfile
// ============================================================================

export async function getInstagramUserProfile(
  igsid: string,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<UserProfile> {
  validateUserId(igsid)
  assertAccessToken(accessToken)

  // Access token goes in the Authorization header — never in the URL — so
  // Cloudflare edge / proxy logs never see it. `encodeURIComponent` on the
  // IGSID blocks path traversal tricks (IGSIDs are numeric strings so the
  // encoding is a no-op in practice but belt-and-braces for future ids).
  const url = new URL(`${INSTAGRAM_GRAPH_API_BASE}/${encodeURIComponent(igsid)}`)
  url.searchParams.set('fields', PROFILE_FIELDS)

  const response = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const raw = (await response.json()) as InstagramUserProfile
  return mapProfile(igsid, raw)
}

/**
 * Adapter-level bridge used by the capability methods. Accepts the generic
 * `FetchUserProfileArgs` shape and delegates to `getInstagramUserProfile`.
 */
export async function fetchUserProfile(
  args: FetchUserProfileArgs,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<UserProfile> {
  return getInstagramUserProfile(args.userId, accessToken, fetchImpl)
}

// ============================================================================
// Mapping — IG profile → capability `UserProfile`
// ============================================================================

function mapProfile(igsid: string, raw: InstagramUserProfile): UserProfile {
  const name = (raw.name ?? '').trim()
  const username = (raw.username ?? '').trim()

  // Prefer display name, else @username, else fall back to the IGSID so the
  // chat UI always has SOMETHING to render.
  const displayName =
    name.length > 0
      ? name
      : username.length > 0
        ? `@${username}`
        : igsid

  const profile: UserProfile = {
    id: igsid,
    name: displayName,
    avatarUrl: raw.profile_pic,
    extra: {
      id: igsid,
      username: username.length > 0 ? username : undefined,
      rawName: name.length > 0 ? name : undefined,
    },
  }
  return profile
}

// ============================================================================
// Internal helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<InstagramDmError> {
  let envelope: InstagramErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as InstagramErrorEnvelope
  } catch {
    // Non-JSON error body (edge gateway HTML etc.) — leave envelope null.
  }
  const retryAfterMs = parseInstagramRetryAfterMs(
    response.headers.get('Retry-After'),
  )
  return classifyInstagramError(envelope, response.status, retryAfterMs)
}

function validateUserId(userId: string | undefined | null): void {
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    throw new InstagramDmError(
      'INVALID_PAYLOAD',
      'getInstagramUserProfile: igsid is required',
    )
  }
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new InstagramDmError('INVALID_PAYLOAD', 'accessToken is required')
  }
}
